import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import Editable from "@/components/Editable";
import { ArrowRight, GraduationCap, PlayCircle, MapPin, Devices } from "@phosphor-icons/react";

export default function CoursesChooser() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="courses-chooser">
      <Seo title="Browse Courses | Puppl" description="Pick your training style: in-person group classes on the Sunshine Coast or online courses you can take anywhere." canonical={typeof window !== "undefined" ? window.location.origin + "/courses" : ""} />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 text-center">
          <Editable id="courses_chooser.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-2" defaultHtml="Browse courses" />
          <Editable id="courses_chooser.heading" as="h1" className="font-heading text-4xl lg:text-6xl font-bold leading-tight" defaultHtml="Pick how you&#39;d like to learn." />
          <Editable id="courses_chooser.subtitle" as="p" className="text-lg opacity-90 mt-3 max-w-2xl mx-auto" defaultHtml="Small-group classes here on the Coast, or self-paced courses you can take from anywhere — same fear-free, evidence-based methods, same the team." />
        </div>
      </section>

      <section className="py-16 lg:py-24 px-6 lg:px-12 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {/* IN-PERSON CARD */}
          <Link
            to="/courses/in-person"
            data-testid="chooser-in-person"
            className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-[#f38838] text-white p-8 lg:p-10 flex flex-col min-h-[380px]"
          >
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/10" />
            <div className="absolute -bottom-16 -left-12 w-72 h-72 rounded-full bg-white/5" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-5">
                <MapPin size={28} weight="duotone" />
              </div>
              <Editable id="courses_chooser.inperson.eyebrow" as="p" className="text-xs uppercase tracking-wider font-bold opacity-80 mb-2" defaultHtml="In-person" />
              <Editable id="courses_chooser.inperson.heading" as="h2" className="font-heading text-3xl lg:text-4xl font-bold leading-tight mb-3" defaultHtml="Train with the team at one of our halls or at your home." />
              <Editable id="courses_chooser.inperson.body" as="p" className="opacity-90 mb-6 flex-1" defaultHtml="Small group classes at Mudjimba, Kawana &amp; Eudlo. Puppy school, teenage classes, scent detection and more." />
              <Editable
                id="courses_chooser.inperson.bullets"
                as="ul"
                className="space-y-1.5 mb-6 text-sm opacity-90"
                defaultHtml="<li>• 6 puppies per class max</li><li>• Fear-free, evidence-based</li><li>• Reports after every session</li>"
              />
            </div>
            <div className="relative inline-flex items-center gap-2 font-heading font-semibold mt-auto">
              <Editable id="courses_chooser.inperson.cta" inline as="span" defaultHtml="See upcoming intakes" /> <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          {/* ONLINE CARD */}
          <Link
            to="/courses/online"
            data-testid="chooser-online"
            className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-[#1b4a76] text-white p-8 lg:p-10 flex flex-col min-h-[380px]"
          >
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/10" />
            <div className="absolute -bottom-16 -left-12 w-72 h-72 rounded-full bg-white/5" />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-5">
                <Devices size={28} weight="duotone" />
              </div>
              <Editable id="courses_chooser.online.eyebrow" as="p" className="text-xs uppercase tracking-wider font-bold opacity-80 mb-2" defaultHtml="Online" />
              <Editable id="courses_chooser.online.heading" as="h2" className="font-heading text-3xl lg:text-4xl font-bold leading-tight mb-3" defaultHtml="Self-paced courses, anywhere." />
              <Editable id="courses_chooser.online.body" as="p" className="opacity-90 mb-6 flex-1" defaultHtml="Video lessons, downloadable training plans and progress tracking — for puppy families outside the Coast or who can&#39;t make Saturday mornings." />
              <Editable
                id="courses_chooser.online.bullets"
                as="ul"
                className="space-y-1.5 mb-6 text-sm opacity-90"
                defaultHtml="<li>• Step-by-step modules</li><li>• Watch on phone, tablet or laptop</li><li>• Email the team if you get stuck</li>"
              />
            </div>
            <div className="relative inline-flex items-center gap-2 font-heading font-semibold mt-auto">
              <Editable id="courses_chooser.online.cta" inline as="span" defaultHtml="Browse online courses" /> <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>

        <div className="mt-10 text-center text-sm text-stone-500">
          Not sure which? <Link to="/contact" className="text-[#f38838] font-semibold hover:underline">Ask the team</Link> — or chat with our virtual receptionist in the bottom-right.
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
